"use client";

import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { Maximize2, Minimize2 } from "lucide-react";
import { fetcher } from "@/lib/api";
import type { ApiResponse, ChartCandle, StageRecord } from "@/types";
import type { Time } from "lightweight-charts";

interface StockChartProps {
  portfolioName: string;
  stockCode: string;
  stockName: string;
  buyStages?: StageRecord[];
  /** 차트 데이터 소스 URL 베이스 오버라이드 (예: ETF(us)는 fdr 엔드포인트). period 는 자동 append. */
  chartUrlBase?: string;
  /** 초기 화면에 보여줄 봉 수 (기본 200). 데이터가 이보다 적거나 같으면 전체 표시. */
  initialBars?: number;
  /** 이동평균선 목록 (기본 DEFAULT_MA_LINES). 모듈 상수로 넘길 것 — 인라인 배열은 차트 재생성 유발. */
  maLines?: MaLine[];
  /** 표시할 기간 버튼 (기본 5분/일/주/월 전체) */
  periods?: readonly Period[];
  /** VWAP 체크박스 노출 — 체크 시 이평선 대신 VWAP 선 표시 (지수 차트용) */
  enableVwap?: boolean;
}

const STAGE_COLOR = "#ffcc00"; // 노란색
const VWAP_COLOR = "#0891b2";  // VWAP 선 색 (cyan-700)

// 이동평균선 설정 — 20일선은 굵은 오렌지로 강조 (maLines prop 으로 교체 가능)
export type MaLine = { period: number; color: string; lineWidth: number };
export const DEFAULT_MA_LINES: MaLine[] = [
  { period: 5, color: "#ec4899", lineWidth: 1 },   // 핑크
  { period: 20, color: "#f97316", lineWidth: 3 },  // 굵은 오렌지
  { period: 60, color: "#16a34a", lineWidth: 1 },  // 초록
  { period: 120, color: "#8b5cf6", lineWidth: 1 }, // 보라
];

const ALL_PERIODS = ["5m", "D", "W", "M"] as const;
type Period = (typeof ALL_PERIODS)[number];

// 단순이동평균 — n-1번째 봉까지는 값 없음
function sma(candles: { time: string | number; close: number }[], n: number) {
  const out: { time: Time; value: number }[] = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= n) sum -= candles[i - n].close;
    if (i >= n - 1) out.push({ time: candles[i].time as Time, value: sum / n });
  }
  return out;
}

// 누적 VWAP — typical=(H+L+C)/3 을 거래량 가중 누적평균. 거래량 0(지수·FRED 등)이면 typical 누적평균으로 폴백.
function vwapSeries(candles: ChartCandle[]) {
  const out: { time: Time; value: number }[] = [];
  let pv = 0, vv = 0, tSum = 0;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const typical = (c.high + c.low + c.close) / 3;
    const vol = c.volume > 0 ? c.volume : 0;
    pv += typical * vol; vv += vol; tSum += typical;
    out.push({ time: c.time as Time, value: vv > 0 ? pv / vv : tSum / (i + 1) });
  }
  return out;
}

export function StockChart({ portfolioName, stockCode, stockName, buyStages = [], chartUrlBase, initialBars = 200, maLines = DEFAULT_MA_LINES, periods = ALL_PERIODS, enableVwap = false }: StockChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof import("lightweight-charts").createChart> | null>(null);
  const [period, setPeriod] = useState<Period>("D");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [vwapOn, setVwapOn] = useState(false);  // VWAP 체크 시 이평선 대신 VWAP 선
  const showVwap = enableVwap && vwapOn;

  // 전체 화면일 때 Esc 로 닫기
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setIsFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  const { data } = useSWR<ApiResponse<ChartCandle[]>>(
    stockCode
      ? (chartUrlBase
          ? `${chartUrlBase}&period=${period}`
          : `/api/portfolio/chart?name=${encodeURIComponent(portfolioName)}&code=${encodeURIComponent(stockCode)}&period=${period}`)
      : null,
    fetcher
  );

  const candles = data?.data || [];

  useEffect(() => {
    if (!chartContainerRef.current || candles.length === 0) return;

    let isMounted = true;

    (async () => {
      const { createChart, CandlestickSeries, HistogramSeries, LineSeries, ColorType } = await import("lightweight-charts");

      if (!isMounted || !chartContainerRef.current) return;

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        layout: {
          background: { type: ColorType.Solid, color: "#ffffff" },
          textColor: "rgba(0,0,0,0.55)",
          fontSize: 12,
        },
        grid: {
          vertLines: { color: "rgba(0,0,0,0.06)" },
          horzLines: { color: "rgba(0,0,0,0.06)" },
        },
        crosshair: {
          mode: 0,
        },
        rightPriceScale: {
          borderColor: "rgba(0,0,0,0.12)",
        },
        timeScale: {
          borderColor: "rgba(0,0,0,0.12)",
          timeVisible: period === "5m",
          secondsVisible: false,
          tickMarkFormatter: period === "5m"
            ? (time: number) => {
                const d = new Date(time * 1000);
                const hh = String(d.getHours()).padStart(2, "0");
                const mm = String(d.getMinutes()).padStart(2, "0");
                return `${hh}:${mm}`;
              }
            : undefined,
        },
        localization: period === "5m"
          ? {
              timeFormatter: (time: number) => {
                const d = new Date(time * 1000);
                const yyyy = d.getFullYear();
                const mo = String(d.getMonth() + 1).padStart(2, "0");
                const dd = String(d.getDate()).padStart(2, "0");
                const hh = String(d.getHours()).padStart(2, "0");
                const mm = String(d.getMinutes()).padStart(2, "0");
                return `${yyyy}-${mo}-${dd} ${hh}:${mm}`;
              },
            }
          : undefined,
      });

      chartRef.current = chart;

      // Candlestick
      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: "#ef4444",
        downColor: "#3b82f6",
        borderUpColor: "#ef4444",
        borderDownColor: "#3b82f6",
        wickUpColor: "#ef4444",
        wickDownColor: "#3b82f6",
      });

      candleSeries.setData(candles.map((c) => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })));

      // 매수 차수별 가격 라인 (중복 stage 제거)
      // 기본 title 은 차트 안쪽(캔들 위)에 그려져 우측이 가려지므로 쓰지 않고,
      // 우측 가격축 위에 '가격 N차' HTML 오버레이 라벨을 직접 그린다.
      const uniqueStages = buyStages.filter((s, i, arr) => arr.findIndex((x) => x.stage === s.stage) === i);
      const fmtLabelPrice = (v: number) => (v < 1 ? v.toFixed(4) : v.toFixed(2));
      const stageLabels: { el: HTMLDivElement; price: number }[] = [];
      for (const stage of uniqueStages) {
        if (stage.buy_price > 0) {
          candleSeries.createPriceLine({
            price: stage.buy_price,
            color: STAGE_COLOR,
            lineWidth: 1,
            lineStyle: 2, // Dashed
            axisLabelVisible: false,
            title: "",
          });
          const el = document.createElement("div");
          el.dataset.stageLabel = "1";
          el.textContent = `${fmtLabelPrice(stage.buy_price)} ${stage.stage}차`;
          Object.assign(el.style, {
            position: "absolute",
            right: "0",
            display: "none",
            zIndex: "3",
            pointerEvents: "none",
            background: STAGE_COLOR,
            color: "#000000",
            fontSize: "10px",
            fontWeight: "600",
            padding: "1px 4px",
            borderRadius: "2px",
            whiteSpace: "nowrap",
          });
          chartContainerRef.current!.appendChild(el);
          stageLabels.push({ el, price: stage.buy_price });
        }
      }
      const updateStageLabels = () => {
        for (const { el, price } of stageLabels) {
          const y = candleSeries.priceToCoordinate(price);
          if (y == null || y < 0) {
            el.style.display = "none";
            continue;
          }
          el.style.display = "block";
          el.style.top = `${Math.round(y - 8)}px`;
        }
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(updateStageLabels);

      if (showVwap) {
        // VWAP 선 (이평선 대체)
        const line = chart.addSeries(LineSeries, {
          color: VWAP_COLOR,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        line.setData(vwapSeries(candles));
      } else {
        // 이동평균선
        for (const ma of maLines) {
          const line = chart.addSeries(LineSeries, {
            color: ma.color,
            lineWidth: ma.lineWidth as 1 | 2 | 3 | 4,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          line.setData(sma(candles, ma.period));
        }
      }

      // Volume
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: "volume" },
        priceScaleId: "volume",
      });

      chart.priceScale("volume").applyOptions({
        scaleMargins: { top: 0.85, bottom: 0 },
      });

      volumeSeries.setData(candles.map((c) => ({
        time: c.time,
        value: c.volume,
        color: c.close >= c.open ? "rgba(239,68,68,0.3)" : "rgba(59,130,246,0.3)",
      })));

      // 초기 화면은 최근 initialBars 봉만 표시 — 이전 봉은 왼쪽 스크롤로 확인
      if (candles.length > initialBars) {
        chart.timeScale().setVisibleLogicalRange({ from: candles.length - initialBars, to: candles.length });
      } else {
        chart.timeScale().fitContent();
      }
      requestAnimationFrame(updateStageLabels);

      const ro = new ResizeObserver(() => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
          requestAnimationFrame(updateStageLabels);
        }
      });
      ro.observe(chartContainerRef.current);

      return () => {
        ro.disconnect();
        for (const { el } of stageLabels) el.remove();
      };
    })();

    return () => {
      isMounted = false;
      // 차수 라벨 오버레이 제거 (async 내부 return 은 React 클린업으로 호출되지 않음)
      chartContainerRef.current?.querySelectorAll("[data-stage-label]").forEach((n) => n.remove());
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [candles, buyStages, initialBars, maLines, showVwap]);

  return (
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[100] p-4 flex flex-col"
          : "rounded-lg p-4 h-full flex flex-col"
      }
      style={{ background: "#ffffff" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-900">{stockName}</span>
          <span className="text-xs text-black/55">{stockCode}</span>
          {/* 차수 범례 */}
          {buyStages.filter((s) => s.buy_price > 0).map((s, i) => (
            <span
              key={`${s.stage}-${i}`}
              className="text-xs px-1.5 py-0.5 rounded font-semibold border"
              style={{ color: STAGE_COLOR, background: "rgba(255,140,0,0.18)", borderColor: "rgba(255,140,0,0.45)" }}
            >
              {s.stage}차
            </span>
          ))}
          {/* 이동평균 / VWAP 범례 */}
          <span className="flex items-center gap-1.5 ml-1">
            {showVwap ? (
              <span className="text-2xs" style={{ color: VWAP_COLOR, fontWeight: 700 }}>VWAP</span>
            ) : (
              maLines.map((ma) => (
                <span key={ma.period} className="text-2xs" style={{ color: ma.color, fontWeight: ma.lineWidth > 1 ? 700 : 400 }}>
                  {ma.period}
                </span>
              ))
            )}
          </span>
          {/* VWAP 토글 (지수 차트) — 체크 시 이평선 대신 VWAP 선 */}
          {enableVwap && (
            <label className="flex items-center gap-1 text-2xs text-black/60 cursor-pointer select-none ml-1">
              <input
                type="checkbox"
                checked={vwapOn}
                onChange={(e) => setVwapOn(e.target.checked)}
                className="cursor-pointer"
              />
              VWAP
            </label>
          )}
        </div>
        <div className="flex gap-1">
          {periods.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                period === p
                  ? "bg-blue-600 border-blue-600 text-white font-semibold"
                  : "bg-white border-black/[0.2] text-black/80 hover:text-neutral-900 hover:border-black/[0.35]"
              }`}
            >
              {p === "5m" ? "5분" : p === "D" ? "일봉" : p === "W" ? "주봉" : "월봉"}
            </button>
          ))}
          {/* 전체 화면 토글 */}
          <button
            onClick={() => setIsFullscreen((v) => !v)}
            className="px-2 py-1 rounded-md border bg-white border-black/[0.2] text-black/80 hover:text-neutral-900 hover:border-black/[0.35] transition-colors"
            title={isFullscreen ? "전체 화면 닫기 (Esc)" : "전체 화면"}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </div>
      {/* Chart */}
      <div ref={chartContainerRef} className="relative flex-1 min-h-0" />
      {candles.length === 0 && (
        <div className="flex items-center justify-center h-full">
          <p className="text-xs text-black/45">차트 데이터 로딩중...</p>
        </div>
      )}
    </div>
  );
}
